import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState, useCallback } from 'react'
import { PieChart, Pie, Cell, Sector, ResponsiveContainer, Tooltip } from 'recharts'

import { ChartData } from '../../types'
import { ChartContainer } from '../common/chart'
import { useIsMobile } from '../../hooks/use-mobile'
import { useStableCallback } from '../../hooks/use-stable-callback'
import { PaymentsWithNoveltiesState } from '../../containers/new-insights-management'

export const PaymentStatusChart = ({ paymentsWithNoveltiesState }: { paymentsWithNoveltiesState: PaymentsWithNoveltiesState | null }) => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { t } = useTranslation(['insights-funding-history-management'])

  const [activeIndex, setActiveIndex] = useState(0)
  const [data, setData] = useState<Array<ChartData>>([
    { name: t('insights.charts.paymentStatus.conventions.resolvedApproved'), value: 0, color: '#22C55E' },
    { name: t('insights.charts.paymentStatus.conventions.pending'), value: 0, color: '#F59E0B' },
    { name: t('insights.charts.paymentStatus.conventions.resolvedRejected'), value: 0, color: '#EF4444' }
  ])

  useEffect(() => {
    if (paymentsWithNoveltiesState) {
      const dataUpdated = [...data]
      dataUpdated[0].value = paymentsWithNoveltiesState.paymentsWithNoveltiesApproved
      dataUpdated[1].value = paymentsWithNoveltiesState.paymentsWithNoveltiesPending
      dataUpdated[2].value = paymentsWithNoveltiesState.paymentsWithNoveltiesRejected

      setData(dataUpdated)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsWithNoveltiesState])

  const config = data.reduce((acc, item) => {
    acc[item.name] = { color: item.color, label: item.name }
    return acc
  }, {} as Record<string, { color: string; label: string }>)

  const total = data.reduce((sum, item) => sum + item.value, 0)

  const onPieEnter = useStableCallback((_: any, index: number) => {
    setActiveIndex(index)
  })

  const renderActiveShape = useCallback((props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props

    // Reduce the expansion to 5% of the original size for a more subtle effect
    const expandedOuterRadius = outerRadius + outerRadius * 0.05

    return (
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={expandedOuterRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    )
  }, [])

  const CustomTooltip = useCallback(
    ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const percentage = ((payload[0].value / total) * 100).toFixed(1)
        const count = Math.round((payload[0].value / 100) * 13493) // Based on the 13.493 total payments

        return (
          <div className="bg-white px-4 py-2 rounded-md shadow-lg border border-gray-200 text-sm">
            <p className="text-[#737373] font-medium">{t('insights.charts.paymentDistribution.tooltip', { percentage, count })}</p>
          </div>
        )
      }
      return null
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [total]
  )

  const innerRadiusPercent = isMobile ? '70%' : '75%'
  const outerRadiusPercent = isMobile ? '90%' : '95%'

  const handleViewHistory = useStableCallback(() => {
    navigate('/payments-to-review', { state: { previousPath: 'insights' } })
  })

  return (
    <div
      className="border border-[#E5E7EB] bg-[#F9FAFB] self-stretch flex min-w-full flex-col items-stretch px-3 py-4 md:px-6 md:py-6 rounded-[24px] border-solid"
      style={{ height: '400px' }}
    >
      <div className="flex justify-between items-center">
        <div
          style={{
            fontFamily: 'Open Sans',
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '24px',
            color: '#107569'
          }}
        >
          {t('insights.charts.paymentStatus.title')}
        </div>
        <button
          onClick={handleViewHistory}
          className={`text-[#0e9384] ${isMobile ? 'text-xs' : 'text-lg'} font-semibold`}
        >
          {t('insights.charts.paymentDistribution.viewHistory')}
        </button>
      </div>

      <div className={`${isMobile ? 'h-[180px]' : 'h-[300px]'} w-full mt-4 md:mt-6`}>
        <ChartContainer
          config={config}
          className="h-full aspect-auto mt-0"
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <PieChart>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={innerRadiusPercent}
                outerRadius={outerRadiusPercent}
                startAngle={90}
                endAngle={450}
                dataKey="value"
                onMouseEnter={onPieEnter}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>

      <div className="flex justify-center flex-wrap mt-2 md:mt-6 gap-3 md:gap-8">
        {data.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-1 md:gap-2"
          >
            <div
              className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} rounded-full`}
              style={{ backgroundColor: item.color }}
            />
            <div className={`text-gray-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>{item.name}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
